/**
 * Wire shapes for `/api/v1/calendar/*` and the mappers between them and the UI models.
 *
 * The backend speaks snake_case and ISO-8601 UTC instants; the components speak camelCase and
 * `Date`. Keeping the translation here means a field rename on either side is a one-file change.
 */
import {
  AgentBookingSettings,
  AppointmentType,
  BookingLink,
  DayWindows,
  Office,
  ProjectBookingSettings,
  Weekday,
  WorkWindow,
} from './calendar.models';

/* -------------------------------------------------------------------------- */
/* Appointment types (§1)                                                      */
/* -------------------------------------------------------------------------- */

export interface AppointmentTypeDto {
  id?: string;
  _id?: string;
  project_id?: string | null;
  owner_agent_id?: string | null;
  label: string;
  description?: string | null;
  duration_min: number;
  buffer_min?: number | null;
  price?: number | null;
  confirmation_message?: string | null;
  color_hex?: string | null;
  slug?: string;
  is_active?: boolean;
}

/** Create/update body. `slug` is deliberately absent — the backend derives and de-duplicates it. */
export interface AppointmentTypeInput {
  project_id?: string | null;
  owner_agent_id?: string | null;
  label: string;
  description?: string;
  duration_min: number;
  buffer_min?: number;
  price?: number;
  confirmation_message?: string;
  color_hex?: string;
}

export function toAppointmentType(dto: AppointmentTypeDto): AppointmentType {
  return {
    id: String(dto.id ?? dto._id ?? ''),
    label: dto.label ?? '',
    durationMin: Number(dto.duration_min) || 0,
    bufferMin: Number(dto.buffer_min) || 0,
    colorHex: dto.color_hex ?? undefined,
    ownerAgentId: dto.owner_agent_id ?? undefined,
    slug: dto.slug,
    description: dto.description ?? undefined,
    price: typeof dto.price === 'number' ? dto.price : undefined,
    confirmationMessage: dto.confirmation_message ?? undefined,
  };
}

export function toAppointmentTypeInput(type: Partial<AppointmentType>, projectId: string | null): AppointmentTypeInput {
  return {
    project_id: projectId || null,
    owner_agent_id: type.ownerAgentId || null,
    label: (type.label ?? '').trim(),
    description: type.description?.trim() || undefined,
    duration_min: Number(type.durationMin) || 0,
    buffer_min: Number(type.bufferMin) || 0,
    price: typeof type.price === 'number' && type.price > 0 ? type.price : undefined,
    confirmation_message: type.confirmationMessage?.trim() || undefined,
    color_hex: type.colorHex || undefined,
  };
}

/* -------------------------------------------------------------------------- */
/* Booking settings / working hours (§2)                                       */
/* -------------------------------------------------------------------------- */

/** A window edge arrives as an hour number (`9`, `17.5`) or an `"HH:mm"` string. */
export type WindowEdge = number | string;

export interface DayWindowsDto {
  weekday: number;
  enabled: boolean;
  windows: [WindowEdge, WindowEdge][];
}

export interface BookingSettingsDto {
  project_id?: string | null;
  agent_id: string;
  public_booking_enabled?: boolean;
  slot_duration_min?: number;
  buffer_min?: number;
  booking_slug?: string;
  timezone?: string;
  min_notice_min?: number;
  max_advance_days?: number;
  schedule?: DayWindowsDto[];
  is_default?: boolean;
}

/** `"09:30"` → `9.5`; a number passes straight through. */
export function toHourNumber(edge: WindowEdge): number {
  if (typeof edge === 'number') return Number.isFinite(edge) ? edge : 0;
  const [h, m] = String(edge).split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) + (Number.isFinite(m) ? m : 0) / 60;
}

function toDayWindows(dto: DayWindowsDto): DayWindows {
  const windows = (dto.windows ?? []).map(w => [toHourNumber(w?.[0]), toHourNumber(w?.[1])] as WorkWindow);
  return {
    weekday: (((dto.weekday % 7) + 7) % 7) as Weekday,
    enabled: !!dto.enabled,
    windows: windows.length ? windows : [[9, 17] as WorkWindow],
  };
}

export function toBookingSettings(dto: BookingSettingsDto, projectId: string, agentId: string): AgentBookingSettings {
  return {
    projectId: dto.project_id ?? projectId ?? '',
    agentId: dto.agent_id || agentId,
    publicBookingEnabled: !!dto.public_booking_enabled,
    slotDurationMin: Number(dto.slot_duration_min) || 30,
    bufferMin: Number(dto.buffer_min) || 0,
    schedule: (dto.schedule ?? []).map(toDayWindows),
    bookingSlug: dto.booking_slug ?? '',
    timezone: dto.timezone || undefined,
    minNoticeMin: typeof dto.min_notice_min === 'number' ? dto.min_notice_min : undefined,
    maxAdvanceDays: typeof dto.max_advance_days === 'number' ? dto.max_advance_days : undefined,
    isDefault: !!dto.is_default,
  };
}

export function toBookingSettingsDto(settings: AgentBookingSettings): BookingSettingsDto {
  return {
    project_id: settings.projectId || null,
    agent_id: settings.agentId,
    public_booking_enabled: settings.publicBookingEnabled,
    slot_duration_min: settings.slotDurationMin,
    buffer_min: settings.bufferMin,
    booking_slug: settings.bookingSlug || undefined,
    timezone: settings.timezone || undefined,
    min_notice_min: settings.minNoticeMin,
    max_advance_days: settings.maxAdvanceDays,
    // Hour numbers are accepted as-is, so the editor's `[9, 17]` needs no conversion.
    schedule: settings.schedule.map(d => ({ weekday: d.weekday, enabled: d.enabled, windows: d.windows.map(w => [w[0], w[1]] as [number, number]) })),
  };
}

/* -------------------------------------------------------------------------- */
/* Project-level booking defaults (Project Setup → External Calendar)          */
/* -------------------------------------------------------------------------- */

export interface ProjectBookingSettingsDto {
  id?: string | null;
  project_id?: string | null;
  enabled?: boolean;
  default_duration_min?: number;
  default_buffer_min?: number;
  default_schedule?: DayWindowsDto[];
  /** Read-only — derived server-side from each agent's own settings. Never sent on `PUT`. */
  agent_ids?: string[];
  /** True when nothing has been saved yet and this is the server's placeholder response. */
  is_default?: boolean;
}

export function toProjectBookingSettings(dto: ProjectBookingSettingsDto | null | undefined, projectId: string): ProjectBookingSettings {
  return {
    projectId: dto?.project_id ?? projectId,
    // Server default is `enabled: true` (an unconfigured project's existing links keep working),
    // so an absent field means "on", not "off" — only an explicit `false` turns it off.
    enabled: dto?.enabled !== false,
    defaultDurationMin: Number(dto?.default_duration_min) || 30,
    defaultBufferMin: Number(dto?.default_buffer_min) || 0,
    defaultSchedule: (dto?.default_schedule ?? []).map(toDayWindows),
    agentIds: (dto?.agent_ids ?? []).map(String),
    isDefault: !!dto?.is_default,
  };
}

/** `agent_ids` is deliberately omitted — it's derived server-side and ignored (or rejected) on write. */
export function toProjectBookingSettingsDto(settings: ProjectBookingSettings): ProjectBookingSettingsDto {
  return {
    project_id: settings.projectId || null,
    enabled: settings.enabled,
    default_duration_min: settings.defaultDurationMin,
    default_buffer_min: settings.defaultBufferMin,
    default_schedule: settings.defaultSchedule.map(d => ({
      weekday: d.weekday,
      enabled: d.enabled,
      windows: d.windows.map(w => [w[0], w[1]] as [number, number]),
    })),
  };
}

/* -------------------------------------------------------------------------- */
/* Share booking link (§3)                                                     */
/* -------------------------------------------------------------------------- */

export interface BookingLinkDto {
  scope?: 'agent' | 'project';
  slug?: string;
  type_slug?: string;
  token?: string;
  path?: string;
  url?: string | null;
}

export function toBookingLink(dto: BookingLinkDto): BookingLink {
  return {
    scope: dto.scope === 'project' ? 'project' : 'agent',
    slug: dto.slug ?? '',
    typeSlug: dto.type_slug || undefined,
    token: dto.token ?? '',
    path: dto.path ?? '',
    url: dto.url ?? null,
  };
}

/* -------------------------------------------------------------------------- */
/* Public booking page (§4)                                                    */
/* -------------------------------------------------------------------------- */

/** Office branding resolved server-side (project → office, or agent → office). Task 3. */
export interface OfficeDto {
  name?: string;
  logo_url?: string | null;
  phone?: string | null;
  email?: string | null;
  color_hex?: string | null;
}

export function toOffice(dto: OfficeDto | null | undefined): Office | null {
  if (!dto || !dto.name) return null;
  return {
    name: dto.name,
    logoUrl: dto.logo_url ?? undefined,
    phone: dto.phone ?? undefined,
    email: dto.email ?? undefined,
    colorHex: dto.color_hex ?? undefined,
  };
}

export interface PublicAgentDto {
  full_name?: string;
  photo?: string | null;
  booking_slug?: string;
  timezone?: string;
  office?: OfficeDto | null;
}

export interface PublicSlotDto {
  start: string;
  end: string;
}

export interface PublicAvailabilityDayDto {
  date: string;
  slots: PublicSlotDto[];
}

export interface PublicAvailabilityDto {
  timezone?: string;
  duration_min?: number;
  agent?: PublicAgentDto;
  appointment_type?: AppointmentTypeDto;
  days?: PublicAvailabilityDayDto[];
}

export interface PublicProjectDto {
  id?: string;
  _id?: string;
  name?: string;
  slug?: string;
  primary_color?: string;
  color_hex?: string;
  banner_url?: string | null;
  logo_url?: string | null;
  location?: string | null;
  office?: OfficeDto | null;
}

export interface PublicBookingRequestDto {
  t: string;
  scope: 'agent' | 'project';
  slug: string;
  type_slug?: string;
  booking_slug?: string;
  /** UTC instant; `end` is derived server-side from `duration_min`, so it is never sent. */
  start: string;
  attendee_name: string;
  attendee_email: string;
  attendee_phone?: string;
  notes?: string;
}

export interface PublicBookingResultDto {
  appointment_id?: string;
  google_event_id?: string;
  start?: string;
  end?: string;
  timezone?: string;
  manage_token?: string;
  confirmation_message?: string;
  agent?: PublicAgentDto;
  appointment_type?: AppointmentTypeDto;
  status?: string;
  /** Manage-token lookup only: the booked project's public branding, `null` for a personal calendar. */
  project?: PublicProjectDto | null;
}

/* -------------------------------------------------------------------------- */
/* Batched multi-agent fetch (§5)                                              */
/* -------------------------------------------------------------------------- */

/** Lead/contact linkage hydrated onto each event by the batch endpoint (§7). */
export interface RemmiEventLinkDto {
  lead_id?: string | null;
  contact_id?: string | null;
  project_id?: string | null;
  property_id?: string | null;
  appointment_type_id?: string | null;
  /** Attendance/outcome — `scheduled | attended | no_show | cancelled | rescheduled`. */
  attendance_status?: string | null;
  /** Agent feedback, joined to the same lead/contact — the one place it's stored (Task 13). */
  feedback?: string | null;
}

export interface BatchCalendarDto {
  user_id: string;
  full_name?: string;
  photo?: string | null;
  color?: string | null;
  timezone?: string;
  /** False when the agent never linked Google — distinct from "linked but no events". */
  connected?: boolean;
  items?: BatchEventDto[];
}

export interface BatchEventDto {
  id: string;
  summary?: string;
  description?: string;
  location?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
  recurrence?: string[];
  transparency?: string;
  organizer?: { email?: string };
  extendedProperties?: { private?: { user_id?: string; remmi_block?: string; remmi_block_label?: string } };
  /** Google sub-calendar the event came from, when the backend can attribute it. */
  calendar_id?: string;
  background_color?: string;
  remmi_link?: RemmiEventLinkDto | null;
}

export interface BatchEventsErrorDto {
  user_id: string;
  error: string;
}

export interface BatchEventsDto {
  from?: string;
  to?: string;
  calendars?: BatchCalendarDto[];
  errors?: BatchEventsErrorDto[];
}

/* -------------------------------------------------------------------------- */
/* Block time (§6) and event links (§7)                                        */
/* -------------------------------------------------------------------------- */

export type BlockKind = 'block' | 'working_hours_fence';

export interface BlockDto {
  id?: string;
  _id?: string;
  agent_id?: string;
  start: string;
  end: string;
  title?: string;
  description?: string;
  kind?: BlockKind;
  recurrence?: string[];
  google_event_id?: string;
}

export interface RescheduleHistoryEntryDto {
  previous_start: string;
  previous_end: string;
  moved_by?: string | null;
  moved_at: string;
}

export interface EventLinkDto {
  google_event_id: string;
  user_id?: string;
  lead_id?: string;
  contact_id?: string;
  project_id?: string;
  property_id?: string;
  appointment_type_id?: string;
  attendance_status?: string;
  feedback?: string;
  reschedule_history?: RescheduleHistoryEntryDto[];
}
